// src/components/DoctorProfile/DoctorProfile.tsx
import React from "react";
import { toUrl } from "../../lib/config";
import "../../assets/styles/doctor-profile.scss";

/** Metin blokları için esnek model */
export type DoctorSection =
  | { id: string; title: string; paragraphs: string[]; items?: never }
  | { id: string; title: string; items: string[]; paragraphs?: never };

export interface DoctorProfileData {
  /** Örn: "Opr. Dr. Ümmühan Arıgüloğlu" */
  name: string;
  /** Örn: "Kadın Hastalıkları ve Doğum Uzmanı" */
  shortTitle?: string;

  /** Vurgulu yazmak isterseniz soyadı gibi: "ARIGÜLOĞLU" */
  highlightSurname?: string;

  /** Kahraman görseli (arka plan) - büyük, kaplayan görsel */
  heroBg: string;

  /** Sağda duran şeffaf png/jpg. İstenirse boş bırakılabilir. */
  portrait?: string;

  /** Hero altındaki koyu bantta görünen kısa açıklama */
  intro?: string;

  /** Özgeçmiş / hikâye metinleri. Tek dizi veya çift sütun dizisi. */
  bioColumns?: string[] | [string[], string[]];

  /** Alt kısım bölümleri (Çalışma alanları, Eğitim vb.) */
  sections?: DoctorSection[];

  /** Tema renkleri */
  theme?: {
    primary?: string; // vurgu (yeşil/mavi)
    dark?: string;    // koyu bant rengi
  };
}

export interface DoctorProfileProps {
  /** Bütün veriler tek obje halinde dışarıdan gelir. */
  data: DoctorProfileData;
  /** Alt container genişliği (varsayılan 1200px) */
  maxWidth?: number;
}

const DoctorProfile: React.FC<DoctorProfileProps> = ({ data, maxWidth = 1200 }) => {
  const {
    name,
    shortTitle,
    highlightSurname,
    heroBg,
    portrait,
    intro,
    bioColumns,
    sections,
    theme,
  } = data;

  const styleVars: React.CSSProperties = {
    // CSS değişkenleri
    ["--dp-primary" as any]: theme?.primary ?? "#2cadd2",
    ["--dp-dark" as any]: theme?.dark ?? "#0f1b3a",
    ["--dp-max" as any]: `${maxWidth}px`,
  };

  // isim içinden vurgulu kısmı ikinci satıra almak isterseniz highlightSurname kullanın.
  const nameFirstLine = highlightSurname
    ? name.replace(highlightSurname, "").trim()
    : name;

  // Image with raw/prefixed toggle fallback (as requested)
  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 260 }) => {
    const baseIn = src || '';
    // If src is UUID or ends with UUID, use /api/media/:id as raw
    const uuidMatch = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = uuidMatch ? `/api/media/${uuidMatch[1]}` : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', alignItems: 'center', display: 'block' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const current = el.getAttribute('src') || '';
          const next = current === pref ? raw : pref;
          if (current !== next) {
            el.src = next;
            setCurrentSrc(next);
          }
        }}
      />
    );
  };

  return (
    <section className="doctor-profile" style={styleVars}>
      {/* HERO */}
      <div className="dp-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="dp-hero-inner">
          {shortTitle && <div className="dp-kicker">{shortTitle}</div>}
          <h1 className="dp-title">
            <span className="dp-name-line">{nameFirstLine}</span>
            {highlightSurname && (
              <span className="dp-name-highlight">{highlightSurname}</span>
            )}
          </h1>
        </div>

        {!!portrait && (
          <div className="dp-portrait">
            <ImgWithFallback src={portrait} alt={`${name} portre`} height={260} />
          </div>
        )}
      </div>

      {/* INTRO – koyu bant */}
      {intro && (
        <div className="dp-introBand">
          <div className="dp-container">
            <p>{intro}</p>
          </div>
        </div>
      )}

      {/* BODY */}
      <div className="dp-container dp-body">
        {/* Özgeçmiş / Hikâye */}
        {!!bioColumns && (
          <section className="dp-bio">
            {Array.isArray(bioColumns[0]) ? (
              // İki sütun
              <div className="dp-bio-grid">
                {(bioColumns as [string[], string[]]).map((col, idx) => (
                  <div className="dp-bio-col" key={idx}>
                    {col.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              // Tek sütun
              <div className="dp-bio-col">
                {(bioColumns as string[]).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Alt bölümler */}
        {!!sections?.length && (
          <div className="dp-sections">
            {sections.map((s) => (
              <section className="dp-section" key={s.id}>
                <h3 className="dp-section-title">{s.title}</h3>

                {"paragraphs" in s && s.paragraphs?.length ? (
                  <div className="dp-prose">
                    {s.paragraphs.map((t, i) => (
                      <p key={i}>{t}</p>
                    ))}
                  </div>
                ) : null}

                {"items" in s && s.items?.length ? (
                  <ul className="dp-list">
                    {s.items.map((li, i) => (
                      <li key={i}>{li}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default DoctorProfile;
